import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import PriceAlertModal from './PriceAlertModal';
import './AlertsManager.css';

const AlertsManager = () => {
    const { user } = useAuth();
    const { showToast } = useToast();
    
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('active'); // 'active', 'triggered', 'all'
    const [alertModal, setAlertModal] = useState({ isOpen: false, token: null });
    const [stats, setStats] = useState({
        total_alerts: 0,
        active_alerts: 0,
        triggered_alerts: 0,
        inactive_alerts: 0
    });

    useEffect(() => {
        if (user) {
            fetchAlerts();
            fetchStats();
        }
    }, [user, filter]);

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const includeTriggered = filter === 'triggered' || filter === 'all';
            
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch(`/api/alerts/user?includeTriggered=${includeTriggered}`, {
                headers: {
                    'Authorization': `Bearer ${tokens.access_token || ''}`
                }
            });

            const result = await response.json();

            if (result.success) {
                let filteredAlerts = result.alerts;
                
                if (filter === 'active') {
                    filteredAlerts = result.alerts.filter(alert => 
                        alert.is_active && !alert.triggered_at
                    );
                } else if (filter === 'triggered') {
                    filteredAlerts = result.alerts.filter(alert => alert.triggered_at);
                }
                
                setAlerts(filteredAlerts);
            } else {
                throw new Error(result.message || 'Failed to fetch alerts');
            }

        } catch (error) {
            console.error('Error fetching alerts:', error);
            showToast(error.message || 'Failed to load alerts', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch('/api/alerts/stats', {
                headers: {
                    'Authorization': `Bearer ${tokens.access_token || ''}`
                }
            });

            const result = await response.json();

            if (result.success) {
                setStats(result.stats);
            }

        } catch (error) {
            console.error('Error fetching alert stats:', error);
        }
    };

    const handleDeleteAlert = async (alertId) => {
        if (!window.confirm('Are you sure you want to delete this alert?')) {
            return;
        }

        try {
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch(`/api/alerts/${alertId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${tokens.access_token || ''}`
                }
            });

            const result = await response.json();

            if (result.success) {
                showToast('Alert deleted successfully', 'success');
                fetchAlerts();
                fetchStats();
            } else {
                throw new Error(result.message || 'Failed to delete alert');
            }

        } catch (error) {
            console.error('Error deleting alert:', error);
            showToast(error.message || 'Failed to delete alert', 'error');
        }
    };

    const handleToggleAlert = async (alertId, currentStatus) => {
        try {
            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch(`/api/alerts/${alertId}/toggle`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.access_token || ''}`
                },
                body: JSON.stringify({ isActive: !currentStatus })
            });

            const result = await response.json();

            if (result.success) {
                showToast(`Alert ${!currentStatus ? 'activated' : 'deactivated'}`, 'success');
                fetchAlerts();
                fetchStats();
            } else {
                throw new Error(result.message || 'Failed to update alert');
            }

        } catch (error) {
            console.error('Error toggling alert:', error);
            showToast(error.message || 'Failed to update alert', 'error');
        }
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(6)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const getAlertTypeLabel = (alertType) => {
        switch (alertType) {
            case 'price_above':
                return 'Price Above';
            case 'price_below':
                return 'Price Below';
            case 'percentage_change':
                return 'Percentage Change';
            default:
                return alertType;
        }
    };

    const getAlertIcon = (alertType, isTriggered) => {
        if (isTriggered) return '✅';
        
        switch (alertType) {
            case 'price_above':
                return '🚀';
            case 'price_below':
                return '📉';
            case 'percentage_change':
                return '📊';
            default:
                return '🔔';
        }
    };

    const openCreateAlert = () => {
        setAlertModal({ isOpen: true, token: null });
    };

    const closeAlertModal = () => {
        setAlertModal({ isOpen: false, token: null });
        fetchAlerts();
        fetchStats();
    };

    if (!user) {
        return (
            <div className="alerts-manager">
                <div className="auth-required">
                    <h3>Authentication Required</h3>
                    <p>Please log in to manage your price alerts.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="alerts-manager">
            <div className="alerts-header">
                <div className="header-content">
                    <h2>Price Alerts</h2>
                    <p>Manage your token price alerts and notifications</p>
                </div>
                <button className="create-alert-btn" onClick={openCreateAlert}>
                    + Create Alert
                </button>
            </div>

            {/* Alert Statistics */}
            <div className="alert-stats">
                <div className="stat-card">
                    <div className="stat-value">{stats.active_alerts}</div>
                    <div className="stat-label">Active Alerts</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats.triggered_alerts}</div>
                    <div className="stat-label">Triggered</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats.total_alerts}</div>
                    <div className="stat-label">Total Alerts</div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs">
                <button
                    className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
                    onClick={() => setFilter('active')}
                >
                    Active ({stats.active_alerts})
                </button>
                <button
                    className={`filter-tab ${filter === 'triggered' ? 'active' : ''}`}
                    onClick={() => setFilter('triggered')}
                >
                    Triggered ({stats.triggered_alerts})
                </button>
                <button
                    className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All ({stats.total_alerts})
                </button>
            </div>

            {/* Alerts List */}
            <div className="alerts-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading alerts...</p>
                    </div>
                ) : alerts.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🔔</div>
                        <h3>No alerts found</h3>
                        <p>
                            {filter === 'active' 
                                ? "You don't have any active price alerts. Create one to get notified when token prices change."
                                : filter === 'triggered'
                                ? "No alerts have been triggered yet."
                                : "You haven't created any price alerts yet."
                            }
                        </p>
                        {filter === 'active' && (
                            <button className="create-first-alert-btn" onClick={openCreateAlert}>
                                Create Your First Alert
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="alerts-list">
                        {alerts.map(alert => (
                            <div key={alert.id} className={`alert-card ${alert.triggered_at ? 'triggered' : ''} ${!alert.is_active ? 'inactive' : ''}`}>
                                <div className="alert-main">
                                    <div className="alert-icon">
                                        {getAlertIcon(alert.alert_type, !!alert.triggered_at)}
                                    </div>
                                    
                                    <div className="alert-info">
                                        <div className="token-info">
                                            <span className="token-symbol">{alert.token_symbol}</span>
                                            {alert.token_name && (
                                                <span className="token-name">({alert.token_name})</span>
                                            )}
                                        </div>
                                        
                                        <div className="alert-details">
                                            <span className="alert-type">{getAlertTypeLabel(alert.alert_type)}</span>
                                            {alert.target_price && (
                                                <span className="target-price">
                                                    Target: {formatPrice(alert.target_price)}
                                                </span>
                                            )}
                                            {alert.percentage_threshold && (
                                                <span className="percentage-threshold">
                                                    Threshold: {alert.percentage_threshold}%
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="alert-meta">
                                            <span className="created-date">
                                                Created: {formatDate(alert.created_at)}
                                            </span>
                                            {alert.triggered_at && (
                                                <span className="triggered-date">
                                                    Triggered: {formatDate(alert.triggered_at)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="alert-actions">
                                    {!alert.triggered_at && (
                                        <button
                                            className={`toggle-btn ${alert.is_active ? 'active' : 'inactive'}`}
                                            onClick={() => handleToggleAlert(alert.id, alert.is_active)}
                                            title={alert.is_active ? 'Deactivate alert' : 'Activate alert'}
                                        >
                                            {alert.is_active ? '⏸️' : '▶️'}
                                        </button>
                                    )}
                                    
                                    <button
                                        className="delete-btn"
                                        onClick={() => handleDeleteAlert(alert.id)}
                                        title="Delete alert"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Price Alert Modal */}
            <PriceAlertModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                token={alertModal.token}
            />
        </div>
    );
};

export default AlertsManager;